/**
 * @module cercaliagl/view/VNavBar
 */

import $ from 'jquery';
import BaseView, { EventType as BaseViewEventType } from './BaseView';
import { createUid, isMobile } from '../util';
import VWidgetSearch, { EventType as WidgetSearchEventType } from './VWidgetSearch';
import VWidgetRouting, { EventType as WidgetRoutingEventType } from './VWidgetRouting';
import VWidgetPois, { EventType as WidgetPoisEventType } from './VWidgetPois';

/**
 * @enum {string}
 */
export const EventType = {
  ADDRESS: 'address',
  ROUTING: 'routing',
  POIS: 'pois',
  RESET: 'reset',
  MORE: 'more',
  CLOSE_MORE: 'close_more',
};

/**
 * @enum {string}
 */
export const View = {
  NAVBAR: 'navbar',
  ADDRESS: 'address',
  ROUTING: 'routing',
  POIS: 'pois',
};

/**
 * @type {Object.<string, string>}
 */
export const Buttons = {
  /**
   * @api
   */
  ADDRESS: 'navMenu',
  /**
   * @api
   */
  POIS: 'navPois',
  /**
   * @api
   */
  ROUTING: 'navRouting',
  /**
   * @api
   */
  MORE: 'navMore',
  /**
   * @api
   */
  CLOSE_MORE: 'navCloseMore',
};

/**
 * @classdesc
 */
class VNavBar extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const uid = createUid();
    const autocompleteId = `navBarAutocomplete${uid}`;
    const jqElement = $(
      `<div class="v-navbar mdc-elevation--z4">
        <div id="${autocompleteId}"></div>
        <div class="mbl-more-btns">
          <button type="button" class="mdc-icon-button ${Buttons.MORE} more">
            <span class="mdc-icon-button__icon icon-more-vert"></span>
          </button>
          <button type="button" class="mdc-icon-button ${Buttons.CLOSE_MORE} close-more">
            <span class="mdc-icon-button__icon icon-close"></span>
          </button>
        </div>
        <div class="mbl-floating-btns">
          <button type="button" class="mdc-icon-button ${Buttons.ADDRESS} mdc-elevation--z4">
            <span class="mdc-icon-button__icon icon-menu"></span>
          </button>
          <button type="button" class="mdc-icon-button ${Buttons.ROUTING} mdc-elevation--z4">
            <span class="mdc-icon-button__icon icon-routing"></span>
          </button>
          <button type="button" class="mdc-icon-button ${Buttons.POIS} mdc-elevation--z4">
            <span class="mdc-icon-button__icon icon-poi"></span>
          </button>
        </div>
        <div class="btns">
          <button type="button" class="mdc-icon-button ${Buttons.ADDRESS}">
            <span class="mdc-icon-button__icon icon-menu"></span>
          </button>
          <button type="button" class="mdc-icon-button ${Buttons.ROUTING}">
            <span class="mdc-icon-button__icon icon-routing"></span>
          </button>
          <button type="button" class="mdc-icon-button ${Buttons.POIS}">
            <span class="mdc-icon-button__icon icon-poi"></span>
          </button>
        </div>
       </div>`,
    );

    super(jqElement);

    /**
     * @private
     * @type {string}
     */
    this.autocompleteId_ = autocompleteId;

    /**
     * @private
     * @type {VWidgetSearch}
     */
    this.widgetSearch_ = new VWidgetSearch();

    /**
     * @private
     * @type {VWidgetRouting}
     */
    this.widgetRouting_ = new VWidgetRouting();

    /**
     * @private
     * @type {VWidgetPois}
     */
    this.widgetPois_ = new VWidgetPois();

    /**
     * @private
     * @type {string}
     */
    this.currentView_ = View.NAVBAR;

    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    this.jqElement_.find(`.${Buttons.ADDRESS}`).click(() => this.showAdressMenu());
    this.jqElement_.find(`.${Buttons.ROUTING}`).click(() => this.showRoutingMenu());
    this.jqElement_.find(`.${Buttons.POIS}`).click(() => this.showPoisMenu());
    this.jqElement_.find(`.${Buttons.MORE}`).click(() => this.toggleMoreButtons(true));
    this.jqElement_.find(`.${Buttons.CLOSE_MORE}`).click(() => this.toggleMoreButtons(false));

    this.widgetSearch_.hide();
    this.widgetRouting_.hide();
    this.widgetPois_.hide();

    this.on(BaseViewEventType.ATTACHED, (parentElement) => {
      this.widgetSearch_.appendTo(parentElement);
      this.widgetRouting_.appendTo(parentElement);
      this.widgetPois_.appendTo(parentElement);

      this.widgetSearch_.on(WidgetSearchEventType.CLOSE_MENU, () => this.reset());
      this.widgetRouting_.on(WidgetRoutingEventType.CLOSE_ROUTING, () => this.reset());
      this.widgetPois_.on(WidgetPoisEventType.CLOSE_POIS, () => this.reset());
    });
  }

  /**
   * Resize autocomplete input
   */
  adjustByDevice() {
    this.jqElement_.find('.v-autocomplete input').width(!isMobile() ? null : this.getMapViewport().offsetWidth - 42);
  }

  /**
   * Reset view
   */
  reset() {
    this.widgetRouting_.hide();
    this.widgetPois_.hide();
    this.widgetSearch_.hide();
    this.jqElement_.show();
    this.currentView_ = View.NAVBAR;
    this.emit(EventType.RESET);
  }

  /**
   * @param {string} button Button
   */
  showButton(button) {
    this.jqElement_.find(`.${button}`).show();
  }

  /**
   * @param {string} button Button
   */
  hideButton(button) {
    this.jqElement_.find(`.${button}`).hide();
  }

  /**
   * @return {VWidgetSearch} Widget search
   */
  getWidgetSearch() {
    return this.widgetSearch_;
  }

  /**
   * @return {VWidgetRouting} Widget routing
   */
  getWidgetRouting() {
    return this.widgetRouting_;
  }

  /**
   * @return {VWidgetPois} Widget POIs
   */
  getWidgetPois() {
    return this.widgetPois_;
  }

  /**
   * @return {string} autocomplete
   */
  getAutocompleteId() {
    return this.autocompleteId_;
  }

  /**
   * Show address menu
   */
  showAdressMenu() {
    this.jqElement_.hide();
    this.widgetRouting_.hide();
    this.widgetPois_.hide();
    this.widgetSearch_.show();
    this.emit(EventType.ADDRESS);
    this.currentView_ = View.ADDRESS;
  }

  /**
   * Show routing menu
   */
  showRoutingMenu() {
    this.jqElement_.hide();
    this.widgetSearch_.hide();
    this.widgetPois_.hide();
    this.widgetRouting_.toggleSettings(false);
    this.widgetRouting_.show();
    this.emit(EventType.ROUTING);
    this.currentView_ = View.ROUTING;
  }

  /**
   * Show pois menu
   */
  showPoisMenu() {
    this.jqElement_.hide();
    this.widgetSearch_.hide();
    this.widgetRouting_.hide();
    this.widgetPois_.show();
    this.emit(EventType.POIS);
    this.currentView_ = View.POIS;
  }

  /**
   * @param {boolean} visible Visible
   */
  toggleMoreButtons(visible) {
    if (visible) {
      this.jqElement_.find(`.${Buttons.MORE}`).hide();
      this.jqElement_.find(`.${Buttons.CLOSE_MORE}`).show();
      this.jqElement_.find('.mbl-floating-btns').show();
    } else {
      this.jqElement_.find(`.${Buttons.CLOSE_MORE}`).hide();
      this.jqElement_.find(`.${Buttons.MORE}`).show();
      this.jqElement_.find('.mbl-floating-btns').hide();
    }
  }

  /**
   * @return {string} Current view
   */
  getCurrentView() {
    return this.currentView_;
  }
}

export default VNavBar;
